import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { Get, Patch } from "libs/services";
import { openToast } from "./toast";
import { processPaginatedItems } from "src/common/libs/utils/items";
import { BaseResponse } from "src/common/types/response/BasePaginationResponse";
import { ITEM } from "src/common/libs/config";
import { StoreMerchantItemsResponse } from "src/common/types/response/storeMerchantItems";
import translate from "src/common/libs/locale";
import { QUERY_UTIL } from "src/common/libs/utils/dataUtils";
import { getFileFromUri } from "src/common/libs/utils/image";
import { reportLog } from "src/common/libs/utils/logs";

interface StoreMerchantItems {
  data?: BaseResponse<StoreMerchantItemsResponse>;
  dataDetail?: BaseResponse<StoreMerchantItemsResponse>;
  loading: boolean;
  page: number;
}

export const getMerchantStoreItems = createAsyncThunk(
  "items/getMerchantStoreItems",
  async (
    {
      id,
      param = { page: 1, limit: QUERY_UTIL.limit },
    }: { id: string; param?: any },
    { rejectWithValue }
  ) => {
    try {
      const query = {
        ...param,
        limit: QUERY_UTIL.limit
      };
      const response = await Get<BaseResponse<StoreMerchantItemsResponse>>(
        `${ITEM.getStoreItems}/${id}`,
        query
      );
      return { data: response.data, page: param?.page || 1 };
    } catch (error) {
      reportLog({
        error: error,
        location: "features/merchantStoreItems->getMerchantStoreItems",
        body: { id, param },
      });
      return rejectWithValue(param?.page || 1);
    }
  }
);

export const getMerchantStoreItem = createAsyncThunk(
  "items/getMerchantStoreItem",
  async (
    {
      id,
      param = { page: 1, limit: QUERY_UTIL.limit },
    }: { id: string; param?: any },
    { rejectWithValue }
  ) => {
    try {
      const query = {
        ...param,
      };
      const response = await Get<BaseResponse<StoreMerchantItemsResponse>>(
        `${ITEM.getStoreItems}/${id}`,
        query
      );
      return { data: response.data, page: param?.page || 1 };
    } catch (error) {
      reportLog({
        error: error,
        location: "features/merchantStoreItems->getMerchantStoreItem",
        body: { id, param },
      });
      return rejectWithValue(param?.page || 1);
    }
  }
);

export const updateMerchantStoreItem = createAsyncThunk(
  "items/updateMerchantStoreItem",
  async ({ body = {} }: { body: any }, { rejectWithValue }) => {
    try {
      const response = await Patch<BaseResponse<StoreMerchantItemsResponse>>(
        `${ITEM.add}`,
        body
      );
      return { data: response.data, body };
    } catch (error) {
      reportLog({
        error: error,
        location: "features/merchantStoreItems->updateMerchantStoreItem",
        body: { body },
      });
      return rejectWithValue(null);
    }
  }
);

export const updateStoreItemImage = createAsyncThunk(
  "items/updateStoreItemImage",
  async ({ id, file }: { id: string; file: string }, thunkAPI) => {
    try {
      const formData = new FormData();
      const imageData = await getFileFromUri(file)    
      formData.append("file", imageData); 
      formData.append("_id", id);
      const response = await Patch<BaseResponse<StoreMerchantItemsResponse>>(
        ITEM.image,
        formData,
        null,
        {
          "content-type": "multipart/form-data",
        }
      );
      return { data: response.data, id };
    } catch (error) {
      reportLog({
        error: error,
        location: "features/merchantStoreItems->updateStoreItemImage",
        body: { id, file },
      });
      thunkAPI.dispatch(
        openToast({
          message: translate("failedUploadImage") || error.message,
          type: "fail",
        })
      );
      return null;
    }
  }
);

const initialState: StoreMerchantItems = {
  data: null,
  dataDetail: null,
  loading: true,
  page: 1,
};

export const StoreMerchantItems = createSlice({
  name: "storeMerchantItems",
  initialState,
  reducers: {
    clearData: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getMerchantStoreItems.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(getMerchantStoreItems.fulfilled, (state, action) => {
      const { data, page } = action.payload || {};
      const filteredPayload = processPaginatedItems<any>(data, state.data);
      state.data = {
        ...state.data,
        ...filteredPayload,
      };
      state.page = page;
      state.loading = false;
    });
    builder.addCase(getMerchantStoreItems.rejected, (state, action) => {
      if (action.payload === 1) {
        state.data = {
          ...state.data,
          payload: [],
        };
        state.loading = false;
      } else {
        state.data = null;
        state.loading = false;
      }
    });

    builder.addCase(getMerchantStoreItem.pending, (state, action) => {
      state.loading = true;
      state.dataDetail = null;
    });
    builder.addCase(getMerchantStoreItem.fulfilled, (state, action) => {
      const { data, page } = action.payload || {};
      const filteredPayload = processPaginatedItems<any>(
        data,
        state.dataDetail
      );
      state.dataDetail = {
        ...state.dataDetail,
        ...filteredPayload,
      };
      state.page = page;
      state.loading = false;
    });
    builder.addCase(getMerchantStoreItem.rejected, (state, action) => {
      state.dataDetail = null;
      state.loading = false;
    });

    builder.addCase(updateMerchantStoreItem.fulfilled, (state, action) => {
      const { body } = action.payload || {};
      const newPayload = state.data.payload.map((it) => {
        if (it._id === body._id) {
          return {
            ...it,
            ...body,
          };
        }
        return it;
      });
      state.data = {
        ...state.data,
        payload: newPayload,
      };
      state.dataDetail = {
        ...state.dataDetail,
        payload: newPayload.filter((it) => it._id === body._id),
      };
    });

    builder.addCase(updateStoreItemImage.fulfilled, (state, action) => {
      const { id, data } = action.payload || {};
      const newPayload = state.data.payload.map((it) => {
        if (it._id === id) {
          return {
            ...(data.payload as any as StoreMerchantItemsResponse),
          };
        }
        return it;
      });
      state.data = {
        ...data,
        payload: newPayload,
      };
      state.dataDetail = {
        ...data,
        payload: newPayload.filter((it) => it._id === id),
      };
    });
  },
});

export const { clearData } = StoreMerchantItems.actions;

export default StoreMerchantItems.reducer;
